import { QueryError } from '@graphql-box/helpers';

import { useQuery } from '@graphql-box/react';
import { Chip, type ChipProps, Skeleton } from '@mui/material';
import { useEffect } from 'react';
import { type MovieFormikValues, type TvFormikValues } from '../types.ts';

const queries: Record<'cast' | 'companies' | 'crew', string> = {
  cast: "query GetCastName(\n  $id: ID!\n) {\n  cast: person(id: $id) {\n    name\n  }\n}\n",
  companies: "query GetCompanyName(\n  $id: ID!\n) {\n  companies: company(id: $id) {\n    name\n  }\n}\n",
  crew: "query GetCrewName(\n  $id: ID!\n) {\n  crew: person(id: $id) {\n    name\n  }\n}\n",
};

const formatLabel = (name: string, type: 'cast' | 'companies' | 'crew') => {
  switch (type) {
    case 'cast': {
      return `Cast: ${name}`;
    }

    case 'companies': {
      return `Company: ${name}`;
    }

    case 'crew': {
      return `Crew: ${name}`;
    }
  }
};

export type SelectedChipProps = {
  type: keyof MovieFormikValues | keyof TvFormikValues;
} & ChipProps;

export const SelectedChip = ({ id, label, type, ...rest }: SelectedChipProps) => {
  const isValidType = (t: keyof MovieFormikValues | keyof TvFormikValues): t is 'cast' | 'companies' | 'crew' =>
    t in queries;

  const hasName = (data: unknown): data is { name: string } => !!data && typeof data === 'object' && 'name' in data;
  const query = isValidType(type) ? queries[type] : '';

  const { execute, loading, result } = useQuery(query, {
    loading: true,
  });

  useEffect(() => {
    if (!label && query) {
      void execute({ variables: { id } }, { data: { initiator: 'SelectedChip' } });
    }
    // This is what we want to re-execute on change of
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, label, query]);

  if (result instanceof QueryError) {
    console.error(result);
    return;
  }

  if (result?.errors?.length) {
    console.error(result.errors);
  }

  let dynamicLabel = label;

  if (loading && !label) {
    dynamicLabel = <Skeleton sx={{ fontSize: '0.8125rem' }} variant="text" width={80} />;
  }

  if (!loading && !label && hasName(result?.data[type]) && isValidType(type)) {
    dynamicLabel = formatLabel(result.data[type].name, type);
  }

  return <Chip {...rest} label={dynamicLabel} variant="outlined" />;
};
